import React, { useState } from 'react'
import { useFirestoreDoc, useFirebaseApp } from 'reactfire'
import { Box, CheckBox, Grommet, Paragraph, Text, Heading } from 'grommet'
import { grommet } from 'grommet/themes'
import { deepMerge } from 'grommet/utils'
import { RadarChart } from 'grommet-controls/chartjs'

import 'firebase/performance'
import 'firebase/firestore'
import {
  descriptionComponent,
  genericComponent,
  isRange,
  noEditComponent,
  noEdit,
  numberComponent,
  rangeComponent,
  sumNumbersInArray
} from '../../utils/propertyComponents'

import {
  accountPreferences,
  mappedPreference,
  preferencesWeight
} from '../../utils/emptyProperty'
import { priceHandler } from '../../utils/utils'

function SingleProperty(props) {
  const { userID, docID, userPreferences } = props.location.state
  const [editMode, setEditMode] = useState(false)
  const firebaseApp = useFirebaseApp()
  const propertyRef = firebaseApp
    .firestore()
    .collection(userID)
    .doc(docID)

  const propertyDoc = useFirestoreDoc(propertyRef)
  const propertyObject = propertyDoc.data()
  const propertyKeys = Object.keys(propertyObject)

  const getCorrectComponent = (data, dataRef, editMode = false) => {
    if (
      data === 'description' ||
      data === 'link' ||
      (!editMode && data === 'address') ||
      (!editMode && data === 'price') ||
      (!editMode && data === 'squareMeterPrice')
    ) {
      return
    } else if (!editMode || noEdit.includes(data)) {
      return noEditComponent(data, propertyObject)
    } else if (data === 'description') {
      return descriptionComponent(data, propertyObject)
    } else if (isRange(data)) {
      return rangeComponent(data, propertyObject, propertyRef)
    } else if (Number(propertyObject[data])) {
      return numberComponent(data, dataRef, propertyObject, propertyRef)
    }
    return genericComponent(data, dataRef, propertyObject, propertyRef)
  }

  const propertyData = propertyKeys.map((data, i) => {
    const dataRef = React.createRef()
    const preference = mappedPreference[userPreferences[data]]

    return preference !== 0 ? (
      <li key={i}>{getCorrectComponent(data, dataRef, editMode)}</li>
    ) : null
  })

  const totalAndMaxScores = Object.keys(accountPreferences).map((preference) => {
    const pref = accountPreferences[preference]
    const weight = preferencesWeight[preference]
    const arrayOfPreferences = []
    const arrayOfMaxValues = []

    Object.keys(pref).forEach(function(num) {
      const weightMultiplier =
        userPreferences[num] === 'high'
          ? 1.5
          : userPreferences[num] === 'medium'
          ? 1
          : 0.5

      if (
        typeof propertyObject[num] === 'number' &&
        propertyObject[num] > 0 &&
        propertyObject[num] <= 100
      ) {
        arrayOfPreferences.push(propertyObject[num] * (weight * weightMultiplier))
        arrayOfMaxValues.push(100 * (weight * weightMultiplier))
      } else if (num === 'associationScore') {
        // We should have these in a map and only do it once.
        if (propertyObject[num].startsWith('A++')) {
          arrayOfPreferences.push(150 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('A+')) {
          arrayOfPreferences.push(120 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('A')) {
          arrayOfPreferences.push(100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('B')) {
          arrayOfPreferences.push(50 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('C')) {
          arrayOfPreferences.push(0 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('D')) {
          arrayOfPreferences.push(-100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        }
      } else if (
        num === 'rooms' &&
        propertyObject.rooms > 2 &&
        propertyObject.scoreKidsFriendly > 60
      ) {
        arrayOfPreferences.push(100 * (weight * weightMultiplier))
        arrayOfMaxValues.push(100 * (weight * weightMultiplier))
      } else if (num === 'price' && propertyObject.price < userPreferences.maxPrice) {
        const percentageOfMaxPrice = Math.round(
          (propertyObject.price / userPreferences.maxPrice) * 100
        )

        if (percentageOfMaxPrice < 70) {
          arrayOfPreferences.push(150 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (percentageOfMaxPrice < 80) {
          arrayOfPreferences.push(120 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (percentageOfMaxPrice < 90) {
          arrayOfPreferences.push(100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else {
          arrayOfPreferences.push(90 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        }
      }
    })

    const totalPreferences = sumNumbersInArray(arrayOfPreferences)
    const maxPreferences = sumNumbersInArray(arrayOfMaxValues)

    return { totalPreferences, maxPreferences }
  })

  const totalPreference = sumNumbersInArray(
    totalAndMaxScores.map((total) => total.totalPreferences)
  )
  const maxPreference = sumNumbersInArray(
    totalAndMaxScores.map((total) => total.maxPreferences)
  )

  const totalScores = Math.round((totalPreference / maxPreference) * 100)
  const price = propertyObject['price']

  // Edit Mode
  const boxStyle = {
    position: 'relative',
    display: 'block'
  }
  const switchStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 999
  }
  const switchEditMode = (e) => {
    const check = e.target.checked
    setEditMode(check)
  }
  const customToggleTheme = {
    checkBox: {
      check: {
        radius: '2px'
      },
      toggle: {
        size: '36px'
      },
      gap: 'xsmall',
      size: '18px'
    }
  }

  // This was lazy but all need refactor anyway
  const radarData = Object.keys(accountPreferences).map((preference) => {
    const pref = accountPreferences[preference]
    const weight = preferencesWeight[preference]
    const arrayOfPreferences = []
    const arrayOfMaxValues = []

    Object.keys(pref).forEach(function(num) {
      const weightMultiplier =
        userPreferences[num] === 'high'
          ? 1.5
          : userPreferences[num] === 'medium'
          ? 1
          : 0.5

      if (
        typeof propertyObject[num] === 'number' &&
        propertyObject[num] > 0 &&
        propertyObject[num] <= 100
      ) {
        arrayOfPreferences.push(propertyObject[num] * (weight * weightMultiplier))
        arrayOfMaxValues.push(100 * (weight * weightMultiplier))
      } else if (num === 'associationScore') {
        // We should have these in a map and only do it once.
        if (propertyObject[num].startsWith('A++')) {
          arrayOfPreferences.push(150 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('A+')) {
          arrayOfPreferences.push(120 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('A')) {
          arrayOfPreferences.push(100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('B')) {
          arrayOfPreferences.push(50 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('C')) {
          arrayOfPreferences.push(0 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (propertyObject[num].startsWith('D')) {
          arrayOfPreferences.push(-100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        }
      } else if (
        num === 'rooms' &&
        propertyObject.rooms > 2 &&
        propertyObject.scoreKidsFriendly > 60
      ) {
        arrayOfPreferences.push(100 * (weight * weightMultiplier))
        arrayOfMaxValues.push(100 * (weight * weightMultiplier))
      } else if (num === 'price' && propertyObject.price < userPreferences.maxPrice) {
        const percentageOfMaxPrice = Math.round(
          (propertyObject.price / userPreferences.maxPrice) * 100
        )

        if (percentageOfMaxPrice < 70) {
          arrayOfPreferences.push(150 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (percentageOfMaxPrice < 80) {
          arrayOfPreferences.push(120 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else if (percentageOfMaxPrice < 90) {
          arrayOfPreferences.push(100 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        } else {
          arrayOfPreferences.push(90 * (weight * weightMultiplier))
          arrayOfMaxValues.push(100 * (weight * weightMultiplier))
        }
      }
    })

    const averagePreferences = sumNumbersInArray(arrayOfPreferences)
    const maxPreferences = sumNumbersInArray(arrayOfMaxValues)

    const percentageData = Math.round((averagePreferences / maxPreferences) * 100)

    return percentageData
  })

  const priceDifference = userPreferences.maxPrice - price
  // Ecosia plants one tree for 0.22 EUR
  const plantTrees = Math.round(priceDifference / 23)
  // 10 years of a single deposit and 5% return YoY
  const tenYearInvestment = priceDifference * (1 + 0.05 * 10)
  // The average salary in Stockholm is 26000, which becomes 20604 after taxes
  const monthsNotWorking = Math.floor(priceDifference / 20604)
  // A reform that uses average material from big department stores and workers
  // at 500kr/hour, accounting a 30% deduction on tax return
  const bigReformsQty = Math.floor(priceDifference / 180000)
  // costs between 200k and 300k, that is why we're rounding instead of floor
  const yearsTravelingTheWorld = Math.round(priceDifference / 250000)

  return (
    <Grommet theme={deepMerge(grommet, customToggleTheme)}>
      <Box
        height="calc(100vh - 60px)"
        overflow="auto"
        style={boxStyle}
        pad={{ bottom: 'large' }}
      >
        <Box style={switchStyle} background="#fff" pad="10px" elevation="small">
          <CheckBox
            id="check-box"
            label={
              <>
                <Heading level={2} size="18px">
                  <strong>
                    <span
                      style={{
                        padding: '3px 5px 4px 6px',
                        marginRight: '4px',
                        marginLeft: '-6px',
                        borderRadius: '50%',
                        fontWeight: '400',
                        fontSize: '16px',
                        minWidth: '30px',
                        display: 'inline-block',
                        textAlign: 'center',
                        color: 'white',
                        textShadow: '-2px 1px 1px rgba(0,0,0,.40)',
                        backgroundColor:
                          totalScores > 80
                            ? '#00C781'
                            : totalScores > 66
                            ? '#EECC58'
                            : totalScores > 50
                            ? '#FFAA15'
                            : totalScores > 25
                            ? '#F06666'
                            : '#FF4040'
                      }}
                    >
                      {totalScores}
                    </span>
                    {propertyObject['address']}
                  </strong>
                </Heading>
                <Text size="small" margin={{ left: 'auto' }}>
                  Edit
                </Text>
              </>
            }
            reverse
            size="16px"
            toggle
            onChange={(e) => switchEditMode(e)}
          />
        </Box>
        <Box pad="medium" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <header>
            <Text size="small">
              <strong>Score</strong>: {totalScores}
            </Text>

            <br />
            {price && (
              <>
                <Text size="small">
                  <strong>Price</strong>: {priceHandler(price)}
                </Text>
                <br />
              </>
            )}
            {propertyObject['squareMeterPrice'] && (
              <>
                <Text size="small">
                  <strong>Price per sqm</strong>:{' '}
                  {priceHandler(propertyObject['squareMeterPrice'])}
                </Text>
                <br />
              </>
            )}
            {price && (
              <>
                <Text size="small">
                  <strong>Price per Score</strong>:{' '}
                  {priceHandler(Math.round(price / totalScores))}
                </Text>
                <br />
              </>
            )}
          </header>
          {!editMode && priceDifference > 10000 && (
            <>
              <Paragraph
                style={{ fontSize: '14px', lineHeight: '1.2', marginTop: '10px' }}
              >
                This place costs {priceHandler(priceDifference)} less than what you could
                pay for your dream home. With this extra money you could:
              </Paragraph>
              <ul
                style={{
                  listStyle: 'inside',
                  fontSize: '12px',
                  lineHeight: '1.5',
                  marginTop: '10px'
                }}
              >
                <li>Plant more than {plantTrees} trees.</li>
                <li>
                  Invest for 10 years and get {priceHandler(tenYearInvestment)}{' '}
                  afterwards.
                </li>
                {monthsNotWorking > 0 && (
                  <li>
                    Completely stop working for{' '}
                    {monthsNotWorking >= 12
                      ? `${Math.floor(monthsNotWorking / 12)} year${
                          Math.floor(monthsNotWorking / 12) > 1 ? 's' : ''
                        }${
                          monthsNotWorking % 12 > 0
                            ? ` and ${monthsNotWorking % 12} months.`
                            : '.'
                        }`
                      : `${monthsNotWorking} months.`}
                  </li>
                )}
                {bigReformsQty > 0 && (
                  <li>
                    Completely reform least {bigReformsQty} area{bigReformsQty > 1 && 's'}{' '}
                    in your new home.
                  </li>
                )}
                {yearsTravelingTheWorld > 0 && (
                  <li>
                    Spend {yearsTravelingTheWorld} year{yearsTravelingTheWorld > 1 && 's'}{' '}
                    traveling the entire World
                    {yearsTravelingTheWorld > 1
                      ? ` or go together with ${yearsTravelingTheWorld - 1} other ${
                          yearsTravelingTheWorld > 2 ? 'people' : 'person'
                        } for a year.`
                      : '.'}
                  </li>
                )}
                <li>And so much more!</li>
              </ul>
            </>
          )}
          {!editMode && (
            <RadarChart
              bounds={[
                [0, 7],
                [0, 100]
              ]}
              size={{ width: 'medium', height: 'small' }}
              data={{
                labels: ['Areas', 'Business Value', 'Location', 'Quality'],
                datasets: [
                  {
                    label: propertyObject['address'],
                    data: [radarData[0], radarData[1], radarData[2], radarData[3]],
                    opacity: 0.2
                  },
                  {
                    label: 'Average for Stockholm',
                    data: [72, 40, 85, 66],
                    opacity: 0.2
                  }
                ]
              }}
              options={{
                scale: {
                  ticks: {
                    beginAtZero: true
                  }
                }
              }}
            />
          )}
          <ul>{propertyData}</ul>
        </Box>
      </Box>
    </Grommet>
  )
}

export default SingleProperty
